import React, { useState, useEffect, useCallback } from 'react';
import LeftAccentCard from '../components/LeftAccentCard';
import {
  Box,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Alert,
  Container,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { ReactComponent as ApprovalNavIcon } from '../assets/icons/approval-nav.svg';
import api, { getApiErrorMessage } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { SkillDefinition, SkillTier, CrossStoreSkillItem } from '../types';
import PageHeader from '../components/PageHeader';
import AddItemButton from '../components/AddItemButton';
import RankStar from '../components/RankStar';


// ─── 勤怠承認 ────────────────────────────────────────────────────────────────

interface CheckInLog {
  timestamp: string;
  read_type: 'check_in' | 'check_out';
  device_info: string | null;
}

interface PaymentPreview {
  basic_wage: number;
  overtime_extra: number;
  late_night_extra: number;
  allowance_amount: number;
  transport_amount: number;
  total: number;
}

interface PendingApproval {
  id: string;
  store_user_id: string;
  store_user_name: string;
  check_in_time: string | null;
  check_out_time: string | null;
  worked_hours: number | null;
  worked_minutes: number | null;
  break_minutes?: number;
  hourly_wage: number | null;
  verification_status: string;
  payment_preview: PaymentPreview | null;
  logs: CheckInLog[];
  // 習得スキル付与用（バックエンドが返す場合に利用。未提供なら付与はスキップ）
  application_id?: string | null;
  position_id?: string | null;
  // 飛び込み出勤（シフト未応募）フラグ。true の場合は時給がシフトから補完されないため、
  // 承認前に店長が時給を入力する必要がある（時給未入力なら承認不可）。
  is_walk_in?: boolean;
  // ワーカーのスキルランク（バックエンドが返す場合に行ヘッダーへ表示。未提供なら非表示）
  // ※ 現状の承認APIは tier を返さないため、表示にはAPI拡張が必要（バックエンド依存）
  tier?: SkillTier | null;
}

interface ApprovalListResponse {
  success: boolean;
  pending_count: number;
  approvals: PendingApproval[];
}

// 課題8/114: 承認済み勤怠（承認取消の候補）
interface VerifiedAttendance {
  id: string;
  store_user_id: string;
  store_user_name: string;
  shift_date: string | null;
  check_in_time: string | null;
  check_out_time: string | null;
  confirmed_payment_amount: number | null;
  withdrawal_allocated_amount: number;
  cancellable: boolean;
  verification_status: string;
}

// ─── 定数 ────────────────────────────────────────────────────────────────────

const PRIMARY_BLUE = '#0071e3';
const TEXT_DARK = '#3c3c3c';
const TEXT_GRAY = '#898989';

// ─── ユーティリティ ──────────────────────────────────────────────────────────

const formatWorkedHours = (hours: number | null, minutes: number | null): string => {
  // worked_minutes はDB上「総分数」として格納されているため直接使用する
  // worked_hours が存在しても加算しない（二重計算を防ぐ）
  const totalMinutes = minutes !== null && minutes !== undefined
    ? minutes
    : (hours ?? 0) * 60;
  return (totalMinutes / 60).toFixed(1) + 'h';
};

const toDateLabel = (dt: string | null): string => {
  if (!dt) return '日付不明';
  return format(new Date(dt), 'yyyy.MM.dd', { locale: ja });
};

const groupByDate = (list: PendingApproval[]): { date: string; items: PendingApproval[] }[] => {
  const map = new Map<string, PendingApproval[]>();
  for (const item of list) {
    const key = toDateLabel(item.check_in_time);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
};

const toTimeString = (dt: string | null): string => {
  if (!dt) return '';
  return format(new Date(dt), 'HH:mm', { locale: ja });
};

// ─── コンポーネント ──────────────────────────────────────────────────────────

const AttendanceApprovalPage: React.FC = () => {
  const { currentUser, storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const managerId = currentUser?.uid;
  // ProtectedRoutes 配下で管理トークンは Layout の自動ログインで設定される。
  const skillToken = managementToken || localStorage.getItem('management_token') || '';

  // 勤怠承認 state
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [completedMap, setCompletedMap] = useState<Record<string, { name: string; sokyuSynced: boolean }>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ─── 習得スキル付与 state ───────────────────────────────────────────────
  // 承認対象（work_attendance.id）ごとに、該当職種のスキルカタログと選択状態を保持
  const [skillDefsByApproval, setSkillDefsByApproval] = useState<Record<string, SkillDefinition[]>>({});
  const [selectedSkills, setSelectedSkills] = useState<Record<string, Set<string>>>({});
  const [loadingSkillsId, setLoadingSkillsId] = useState<string | null>(null);
  // 横断スキル認識（050）: approval.id → (skill_definition_id → 他店舗での同義獲得情報)
  const [crossStoreByApproval, setCrossStoreByApproval] = useState<Record<string, Record<string, CrossStoreSkillItem>>>({});

  // 時刻編集ダイアログ
  const [editTarget, setEditTarget] = useState<PendingApproval | null>(null);
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editBreakMinutes, setEditBreakMinutes] = useState('0');
  // 飛び込み出勤の時給入力（円）。空文字＝未入力。
  const [editHourlyWage, setEditHourlyWage] = useState('');
  const [saving, setSaving] = useState(false);

  // 支払い確認ダイアログ
  const [confirmTarget, setConfirmTarget] = useState<PendingApproval | null>(null);

  // ─── 承認取消（課題8/114） state ─────────────────────────────────────────
  const [verifiedList, setVerifiedList] = useState<VerifiedAttendance[]>([]);
  const [cancelTarget, setCancelTarget] = useState<VerifiedAttendance | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // ─── 勤怠承認: データ取得 ─────────────────────────────────────────────────

  const fetchApprovals = useCallback(async () => {
    if (!storeId) return;
    try {
      setLoading(true);
      setErrorMessage(null);
      const data: ApprovalListResponse = await api.attendance.getApprovalList(storeId);
      setApprovals(data.approvals);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      setErrorMessage(getApiErrorMessage(error, '承認待ち一覧の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // 課題8/114: 承認済み勤怠（承認取消の候補）を取得
  const fetchVerified = useCallback(async () => {
    if (!storeId) return;
    try {
      const data = await api.attendance.getVerifiedList(storeId);
      setVerifiedList(data.attendances ?? []);
    } catch (error) {
      // 承認取消は補助機能のため、失敗しても承認フロー全体は止めない
      console.error('Failed to fetch verified list:', error);
    }
  }, [storeId]);

  useEffect(() => {
    fetchApprovals();
    fetchVerified();
  }, [fetchApprovals, fetchVerified]);

  // ─── 承認取消: 実行 ──────────────────────────────────────────────────────
  const handleCancelVerification = async () => {
    if (!cancelTarget || !storeId || !managerId) return;
    setCancelling(true);
    setErrorMessage(null);
    try {
      await api.attendance.cancelVerification(
        cancelTarget.id, storeId, managerId, cancelReason.trim()
      );
      setCancelTarget(null);
      setCancelReason('');
      // 承認取消で当該勤怠は pending に戻るため、両リストを再取得
      await Promise.all([fetchApprovals(), fetchVerified()]);
    } catch (error) {
      // 引出済み/締め済み等は 409 で理由が返るのでそのまま表示
      setErrorMessage(getApiErrorMessage(error, '承認の取消に失敗しました'));
    } finally {
      setCancelling(false);
    }
  };

  // ─── 勤怠承認: 承認 ──────────────────────────────────────────────────────

  // 習得スキルチェックリスト用のカタログを遅延取得（職種が分かる場合は職種で絞り込む）
  const fetchSkillDefsFor = useCallback(async (approval: PendingApproval) => {
    if (!storeId) return;
    if (skillDefsByApproval[approval.id]) return; // 取得済み
    try {
      setLoadingSkillsId(approval.id);
      const defs = await api.skillSettings.getSkillDefinitions(storeId, skillToken, {
        ...(approval.position_id ? { position_id: approval.position_id } : {}),
        active_only: true,
      });
      setSkillDefsByApproval(prev => ({ ...prev, [approval.id]: defs }));
    } catch (error) {
      // スキルカタログ取得失敗は承認フローを阻害しない（空配列として扱う）
      setSkillDefsByApproval(prev => ({ ...prev, [approval.id]: [] }));
    } finally {
      setLoadingSkillsId(null);
    }

    // 横断スキル認識（050）+ 自店既得（QA項目56）: ベストエフォートで取得。
    // already_owned_here は正準化フラグ OFF でも常に有効なため、enabled に関わらず保持する
    // （already_owned_via_other_stores はフラグ OFF 時サーバー側で常に false）。
    // 失敗時は単に表示しない（承認フローは阻害しない）。
    try {
      const cross = await api.skillSettings.getCrossStoreSkills(storeId, approval.store_user_id, skillToken);
      const byDef: Record<string, CrossStoreSkillItem> = {};
      for (const item of cross.items) {
        byDef[item.store_skill_definition_id] = item;
      }
      setCrossStoreByApproval(prev => ({ ...prev, [approval.id]: byDef }));
    } catch (error) {
      // 無視（バッジ・取得済み表示を出さないだけ）
    }
  }, [storeId, skillToken, skillDefsByApproval]);

  const toggleExpand = (approval: PendingApproval) => {
    const id = approval.id;
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next === id) {
        fetchSkillDefsFor(approval);
      }
      return next;
    });
  };

  // 習得スキルのチェック切り替え（既得スキルは対象外＝剥奪も再付与もしない）
  const toggleSkill = (approvalId: string, skillDefinitionId: string) => {
    if (crossStoreByApproval[approvalId]?.[skillDefinitionId]?.already_owned_here) return;
    setSelectedSkills(prev => {
      const current = new Set(prev[approvalId] ?? []);
      if (current.has(skillDefinitionId)) {
        current.delete(skillDefinitionId);
      } else {
        current.add(skillDefinitionId);
      }
      return { ...prev, [approvalId]: current };
    });
  };

  // tier_id ごとにスキル定義をまとめる（tier の sort_order 昇順）
  const groupSkillsByTier = (defs: SkillDefinition[]): { tier: SkillTier; items: SkillDefinition[] }[] => {
    const map = new Map<string, { tier: SkillTier; items: SkillDefinition[] }>();
    for (const def of defs) {
      if (!def.tier) continue; // tier が null/undefined のものはスキップ
      const key = def.tier.tier_id;
      if (!map.has(key)) map.set(key, { tier: def.tier, items: [] });
      map.get(key)!.items.push(def);
    }
    return Array.from(map.values())
      .sort((a, b) => a.tier.sort_order - b.tier.sort_order)
      .map(group => ({
        tier: group.tier,
        items: group.items.sort((a, b) => (a.slot_index ?? 0) - (b.slot_index ?? 0)),
      }));
  };

  const handleApprove = (approval: PendingApproval) => {
    setConfirmTarget(approval);
  };

  const executeApprove = async (approval: PendingApproval) => {
    if (!managerId) return;
    setConfirmTarget(null);
    try {
      setApprovingId(approval.id);
      setErrorMessage(null);
      const result = await api.attendance.verifyAttendance(approval.id, managerId, true, '承認');

      // 習得スキル付与（additive）。application_id が分かる場合のみ実行し、
      // 失敗しても勤怠承認自体は完了済みなのでフローを止めない。
      // 既得スキルは送らない（バックエンドは冪等だが granted/イベント集計を汚さないため）。
      const ownedByDef = crossStoreByApproval[approval.id] ?? {};
      const skillIds = Array.from(selectedSkills[approval.id] ?? []).filter(
        (id) => !ownedByDef[id]?.already_owned_here
      );
      if (approval.application_id && skillIds.length > 0) {
        try {
          await api.application.approveSkills(approval.application_id, skillIds, skillToken);
        } catch (skillError) {
          console.error('Failed to grant skills:', skillError);
        }
      }

      setExpandedId(null);
      setSelectedSkills(prev => { const next = { ...prev }; delete next[approval.id]; return next; });
      setCompletedMap(prev => ({
        ...prev,
        [approval.id]: {
          name: approval.store_user_name,
          sokyuSynced: result?.sokyu_synced ?? false,
        },
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        setApprovals(prev => prev.filter(a => a.id !== approval.id));
        setCompletedMap(prev => { const next = { ...prev }; delete next[approval.id]; return next; });
      }, 2500);
    } catch (error) {
      console.error('Failed to approve attendance:', error);
      setErrorMessage(getApiErrorMessage(error, '承認処理に失敗しました'));
    } finally {
      setApprovingId(null);
    }
  };

  // ─── 勤怠承認: 時刻編集 ──────────────────────────────────────────────────

  const openEditDialog = (approval: PendingApproval) => {
    setEditTarget(approval);
    setEditCheckIn(toTimeString(approval.check_in_time));
    setEditCheckOut(toTimeString(approval.check_out_time));
    setEditBreakMinutes(String(approval.break_minutes ?? 0));
    setEditHourlyWage(approval.hourly_wage != null ? String(approval.hourly_wage) : '');
  };

  const handleSaveTime = async () => {
    if (!editTarget) return;
    // 飛び込み出勤は時給が必須。数値でなければ保存させない。
    const wageNum = editHourlyWage.trim() === '' ? undefined : Number(editHourlyWage);
    if (editTarget.is_walk_in && (wageNum === undefined || !Number.isFinite(wageNum) || wageNum <= 0)) {
      setErrorMessage('飛び込み出勤の承認には時給（正の数）の入力が必要です');
      return;
    }
    // 休憩時間は勤務種別を問わず送信する（シフト勤務でもシフト定義の休憩を上書きできる）。
    // 休憩は打刻されないためシフト定義は予定値でしかなく、実態と異なる場合はここでしか是正できない。
    // 空欄は null を送って上書きを解除＝シフト定義の休憩へ戻す。
    const breakRaw = editBreakMinutes.trim();
    const breakNum = breakRaw === '' ? null : Number(breakRaw);
    if (breakNum !== null && (!Number.isFinite(breakNum) || breakNum < 0)) {
      setErrorMessage('休憩時間は0以上で入力してください');
      return;
    }
    try {
      setSaving(true);
      setErrorMessage(null);
      const result = await api.attendance.updateAttendanceTime(
        editTarget.id,
        editCheckIn || undefined,
        editCheckOut || undefined,
        // シフト勤務はシフト定義の時給がサーバ側の正。時刻編集時に既存値を送り返すと
        // バックエンドで「シフト勤務の時給は変更できません」エラーになるため、
        // 時給は飛び込み出勤のときだけ送信する（休憩時間は勤務種別を問わず送信）。
        editTarget.is_walk_in ? wageNum : undefined,
        breakNum
      );
      setApprovals(prev =>
        prev.map(a =>
          a.id === editTarget.id
            ? {
                ...a,
                check_in_time: result.check_in_time,
                check_out_time: result.check_out_time,
                worked_hours: result.worked_hours,
                worked_minutes: result.worked_minutes,
                break_minutes: result.break_minutes ?? a.break_minutes ?? 0,
                hourly_wage: result.hourly_wage ?? a.hourly_wage,
                payment_preview: result.payment_preview ?? a.payment_preview,
              }
            : a
        )
      );
      setEditTarget(null);
    } catch (error) {
      console.error('Failed to update time:', error);
      setErrorMessage(getApiErrorMessage(error, '時刻の更新に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // ─── レンダリング ─────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PageHeader
        title="承認"
        icon={<ApprovalNavIcon style={{ width: 22, height: 22, color: '#898989' }} />}
      />

      {/* ─── 勤怠承認 ─────────────────────────────────────────────────────── */}
      <Container maxWidth="sm" sx={{ pb: 2, px: 2, pt: 2 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {loading ? (
            <Box display="flex" justifyContent="center" mt={6}>
              <CircularProgress sx={{ color: PRIMARY_BLUE }} />
            </Box>
          ) : approvals.length === 0 ? (
            <Alert severity="info">承認待ちの勤怠記録はありません</Alert>
          ) : (
            <Stack spacing={0}>
              {groupByDate(approvals).map(({ date, items }) => (
                <Box key={date}>
                  <Typography
                    variant="caption"
                    color={TEXT_GRAY}
                    sx={{ display: 'block', mb: 1, mt: 1.5, fontWeight: 500 }}
                  >
                    {date}
                  </Typography>
                  <Stack spacing={1.5}>
                    {items.map(approval => {
                      const isExpanded = expandedId === approval.id;
                      const isApproving = approvingId === approval.id;
                      const isCompleted = !!completedMap[approval.id];

                      if (isCompleted) {
                        return (
                          <Box
                            key={approval.id}
                            sx={{
                              bgcolor: '#00a29a',
                              borderRadius: '7.25px',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography fontSize="13px" fontWeight={600} color="white">
                              {completedMap[approval.id].name}さんの承認が完了しました
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <LeftAccentCard
                          key={approval.id}
                          accentColor={PRIMARY_BLUE}
                          borderColor="#d9eafb"
                          padding={0}
                        >
                          <CardContent sx={{ pl: 2, pr: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography
                                fontSize="13px"
                                fontWeight={600}
                                color={PRIMARY_BLUE}
                                flex={1}
                                noWrap
                              >
                                {approval.store_user_name} さん
                              </Typography>
                              {/* 飛び込み出勤（シフト未応募）バッジ。時給入力が必要なことを示す */}
                              {approval.is_walk_in && (
                                <Chip
                                  label="飛び込み"
                                  size="small"
                                  sx={{
                                    flexShrink: 0,
                                    bgcolor: '#fff3e0',
                                    color: '#c65f00',
                                    borderRadius: '6px',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                              )}
                              {/* ワーカーのランク（APIが tier を返す場合のみ表示・HTMLデザイン準拠） */}
                              {approval.tier && (
                                <RankStar
                                  tierCode={approval.tier.code}
                                  color={approval.tier.color}
                                  label={approval.tier.name}
                                  showLabel
                                  size={18}
                                />
                              )}
                              {/* 詳細（勤務内容の詳細を展開） */}
                              <Button
                                size="small"
                                onClick={() => toggleExpand(approval)}
                                sx={{
                                  flexShrink: 0,
                                  bgcolor: '#e8f1fd',
                                  color: PRIMARY_BLUE,
                                  borderRadius: '10px',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  px: 1.5,
                                  py: 0.25,
                                  minWidth: 0,
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: '#d6e8fb', boxShadow: 'none' },
                                }}
                              >
                                詳細
                              </Button>
                            </Stack>
                          </CardContent>

                          <Collapse in={isExpanded} unmountOnExit>
                            <Box sx={{ px: 2, pb: 2 }}>
                              <Box sx={{ bgcolor: '#f8f8f9', px: 2, py: 1.5, mb: 2, borderRadius: 1 }}>
                                <Typography variant="caption" color={TEXT_GRAY} display="block" mb={0.5}>
                                  就労時間
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="h6" fontWeight="bold" color={TEXT_DARK} sx={{ minWidth: '130px' }}>
                                    {toTimeString(approval.check_in_time) || '--:--'}
                                    {' - '}
                                    {toTimeString(approval.check_out_time) || '--:--'}
                                  </Typography>
                                  <AddItemButton label="編集" showIcon={false} onClick={() => openEditDialog(approval)} />
                                  <Box flex={1} />
                                  <Chip
                                    label={formatWorkedHours(approval.worked_hours, approval.worked_minutes)}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e0e0e0',
                                      color: TEXT_DARK,
                                      borderRadius: '5px',
                                      fontWeight: 600,
                                      fontSize: '0.8rem',
                                    }}
                                  />
                                </Stack>
                              </Box>

                              {/* 支払い概算 */}
                              {approval.payment_preview && (
                                <Box sx={{ bgcolor: '#f0f7ff', px: 2, py: 1.5, mb: 2, borderRadius: 1 }}>
                                  <Typography variant="caption" color={TEXT_GRAY} display="block" mb={0.5}>
                                    支払い概算
                                  </Typography>
                                  <Stack spacing={0.3}>
                                    {approval.hourly_wage != null && (
                                      <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption" color={TEXT_GRAY}>時給</Typography>
                                        <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                          ¥{approval.hourly_wage.toLocaleString()}/h
                                        </Typography>
                                      </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="caption" color={TEXT_GRAY}>給与</Typography>
                                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                        ¥{approval.payment_preview.basic_wage.toLocaleString()}
                                      </Typography>
                                    </Stack>
                                    {approval.payment_preview.overtime_extra > 0 && (
                                      <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption" color={TEXT_GRAY}>法定外残業手当</Typography>
                                        <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                          +¥{approval.payment_preview.overtime_extra.toLocaleString()}
                                        </Typography>
                                      </Stack>
                                    )}
                                    {approval.payment_preview.late_night_extra > 0 && (
                                      <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption" color={TEXT_GRAY}>深夜手当</Typography>
                                        <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                          +¥{approval.payment_preview.late_night_extra.toLocaleString()}
                                        </Typography>
                                      </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="caption" color={TEXT_GRAY}>諸手当</Typography>
                                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                        +¥{approval.payment_preview.allowance_amount.toLocaleString()}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="caption" color={TEXT_GRAY}>交通費</Typography>
                                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                                        +¥{approval.payment_preview.transport_amount.toLocaleString()}
                                      </Typography>
                                    </Stack>
                                    <Box sx={{ borderTop: '1px solid #d9eafb', pt: 0.5, mt: 0.3 }}>
                                      <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption" color={TEXT_DARK} fontWeight={700}>合計</Typography>
                                        <Typography variant="caption" color={PRIMARY_BLUE} fontWeight={700}>
                                          ¥{approval.payment_preview.total.toLocaleString()}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  </Stack>
                                </Box>
                              )}

                              {/* 習得スキル チェックリスト（職種/tierのカタログから選択 → 承認時に付与） */}
                              <Box sx={{ bgcolor: '#f8f8f9', px: 2, py: 1.5, mb: 2, borderRadius: 1 }}>
                                <Typography variant="caption" color={TEXT_GRAY} display="block" mb={0.5}>
                                  習得スキル
                                </Typography>
                                {loadingSkillsId === approval.id ? (
                                  <Box display="flex" justifyContent="center" py={1}>
                                    <CircularProgress size={20} sx={{ color: PRIMARY_BLUE }} />
                                  </Box>
                                ) : (skillDefsByApproval[approval.id]?.length ?? 0) === 0 ? (
                                  <Typography variant="caption" color={TEXT_GRAY}>
                                    付与できるスキルがありません
                                  </Typography>
                                ) : (
                                  <Stack spacing={1.5}>
                                    {groupSkillsByTier(
                                      (skillDefsByApproval[approval.id] || []).filter(
                                        (def) => !approval.tier || (def.tier != null && def.tier.tier_id === approval.tier.tier_id)
                                      )
                                    ).map(({ tier, items }) => (
                                      <Box key={tier.tier_id}>
                                        <Box sx={{ mb: 0.5 }}>
                                          <RankStar tierCode={tier.code} color={tier.color} label={tier.name} showLabel size={18} />
                                        </Box>
                                        <FormGroup sx={{ gap: 0.75 }}>
                                          {items.map((def) => {
                                            const cross = crossStoreByApproval[approval.id]?.[def.skill_definition_id];
                                            // QA項目56: 自店で既得のスキルは「取得済み」表示・チェック不可（剥奪はしない）。
                                            const ownedHere = !!cross?.already_owned_here;
                                            const checked = ownedHere || (selectedSkills[approval.id]?.has(def.skill_definition_id) ?? false);
                                            // 横断認識（050）: 他店舗で同義スキルを獲得済みなら付与判断を後押しするバッジを出す。
                                            const ownedElsewhere = !!cross?.already_owned_via_other_stores && !ownedHere;
                                            return (
                                              <FormControlLabel
                                                key={def.skill_definition_id}
                                                labelPlacement="start"
                                                disabled={ownedHere}
                                                control={
                                                  <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    onChange={() => toggleSkill(approval.id, def.skill_definition_id)}
                                                    sx={{
                                                      color: PRIMARY_BLUE,
                                                      '&.Mui-checked': { color: PRIMARY_BLUE },
                                                      '&.Mui-disabled.Mui-checked': { color: '#9e9e9e' },
                                                    }}
                                                  />
                                                }
                                                label={
                                                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                                    <Typography variant="body2" color={ownedHere ? TEXT_GRAY : TEXT_DARK}>
                                                      {def.name}
                                                    </Typography>
                                                    {ownedHere && (
                                                      <Chip
                                                        size="small"
                                                        label="取得済み"
                                                        title="このスキルは取得済みです（一度取得したスキルは剥奪されません）"
                                                        sx={{
                                                          height: 20,
                                                          fontSize: '0.65rem',
                                                          bgcolor: '#e8f5e9',
                                                          color: '#2e7d32',
                                                          border: '1px solid #a5d6a7',
                                                        }}
                                                      />
                                                    )}
                                                    {ownedElsewhere && (
                                                      <Chip
                                                        size="small"
                                                        label="他店舗で同等スキル獲得済"
                                                        title={cross?.canonical_name ? `同義スキル「${cross.canonical_name}」を他店舗で獲得済み` : undefined}
                                                        sx={{
                                                          height: 20,
                                                          fontSize: '0.65rem',
                                                          bgcolor: '#fff4e5',
                                                          color: '#b26a00',
                                                          border: '1px solid #ffcc80',
                                                        }}
                                                      />
                                                    )}
                                                  </Box>
                                                }
                                                sx={{
                                                  // PDF p.15：白枠の行・チェックは右。取得済み行は薄グレーで区別。
                                                  m: 0,
                                                  width: '100%',
                                                  justifyContent: 'space-between',
                                                  bgcolor: ownedHere ? '#f4f4f5' : '#fff',
                                                  border: '1px solid #e7e7e7',
                                                  borderRadius: '8px',
                                                  px: 1.5,
                                                  py: 0.25,
                                                }}
                                              />
                                            );
                                          })}
                                        </FormGroup>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}
                              </Box>

                              {/* 飛び込み出勤で時給未入力の場合は承認不可。編集ダイアログで時給入力を促す */}
                              {approval.is_walk_in && approval.hourly_wage == null && (
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', color: '#c65f00', mb: 1, fontWeight: 600 }}
                                >
                                  飛び込み出勤です。「編集」から時給を入力すると承認できます。
                                </Typography>
                              )}
                              <Button
                                fullWidth
                                variant="contained"
                                disabled={
                                  isApproving ||
                                  (!!approval.is_walk_in && approval.hourly_wage == null)
                                }
                                onClick={() => handleApprove(approval)}
                                sx={{
                                  bgcolor: PRIMARY_BLUE,
                                  borderRadius: 2,
                                  py: 1.4,
                                  fontWeight: 'bold',
                                  fontSize: '0.95rem',
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: '#0058b7', boxShadow: 'none' },
                                }}
                              >
                                {isApproving ? <CircularProgress size={22} color="inherit" /> : '承認して完了'}
                              </Button>
                            </Box>
                          </Collapse>
                        </LeftAccentCard>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {/* ─── 承認取消（課題8/114: 承認済み勤怠の巻き戻し） ─────────────── */}
          {verifiedList.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" sx={{ color: TEXT_DARK, fontWeight: 700, mb: 0.5 }}>
                承認済みの勤怠（取消）
              </Typography>
              <Typography variant="caption" color={TEXT_GRAY} sx={{ display: 'block', mb: 1.5 }}>
                誤って承認した場合、未引き出し・未締めの勤怠は承認を取り消して承認待ちに戻せます。
              </Typography>
              <Stack spacing={1.5}>
                {verifiedList.map(v => (
                  <LeftAccentCard key={v.id}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_DARK }} noWrap>
                            {v.store_user_name}
                          </Typography>
                          <Typography variant="caption" color={TEXT_GRAY} sx={{ display: 'block' }}>
                            {v.shift_date ?? ''}
                            {v.confirmed_payment_amount != null && ` ・ ¥${v.confirmed_payment_amount.toLocaleString()}`}
                          </Typography>
                        </Box>
                        {v.cancellable ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            sx={{ flexShrink: 0 }}
                            onClick={() => { setCancelTarget(v); setCancelReason(''); }}
                          >
                            承認を取消
                          </Button>
                        ) : (
                          <Chip label="引き出し済み" size="small" sx={{ flexShrink: 0 }} />
                        )}
                      </Box>
                    </CardContent>
                  </LeftAccentCard>
                ))}
              </Stack>
            </Box>
          )}
        </Container>

      {/* 承認取消 確認ダイアログ（課題8/114） */}
      <Dialog
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: TEXT_DARK, fontWeight: 'bold', pb: 1 }}>
          承認を取り消しますか？
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: TEXT_DARK, mb: 2 }}>
            {cancelTarget?.store_user_name} さんの承認済み勤怠を承認待ち（pending）に戻します。
            付与済みの報酬は取り消され、即給連携の訂正が反映されるまで当該企業のウォレットは
            一時的に引き出し不可（凍結）になります。
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="取消理由"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>
            やめる
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelVerification}
            disabled={cancelling || !cancelReason.trim()}
          >
            {cancelling ? <CircularProgress size={20} color="inherit" /> : '承認を取消す'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 時刻編集ダイアログ */}
      <Dialog
        open={!!editTarget}
        onClose={() => !saving && setEditTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: TEXT_DARK, fontWeight: 'bold', pb: 1 }}>
          就労時間を編集
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="出勤時刻"
              type="time"
              value={editCheckIn}
              onChange={e => setEditCheckIn(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label="退勤時刻"
              type="time"
              value={editCheckOut}
              onChange={e => setEditCheckOut(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label="休憩時間（分）"
              type="number"
              value={editBreakMinutes}
              onChange={e => setEditBreakMinutes(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 5 }}
              helperText="シフト定義の休憩時間を上書きします（空欄でシフト定義に戻す）"
            />
            {editTarget?.is_walk_in && (
              <TextField
                label="時給（円）"
                type="number"
                value={editHourlyWage}
                onChange={e => setEditHourlyWage(e.target.value)}
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, step: 10 }}
                error={editHourlyWage.trim() !== '' && !(Number(editHourlyWage) > 0)}
                helperText="飛び込み出勤（シフトなし）は時給の入力が必要です"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditTarget(null)}
            disabled={saving}
            sx={{ color: TEXT_GRAY }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSaveTime}
            variant="contained"
            disabled={
              saving || !editCheckIn || !editCheckOut ||
              (!!editTarget?.is_walk_in && !(Number(editHourlyWage) > 0))
            }
            sx={{
              bgcolor: PRIMARY_BLUE,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0058b7', boxShadow: 'none' },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 支払い確認ダイアログ */}
      <Dialog
        open={!!confirmTarget}
        onClose={() => !approvingId && setConfirmTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: TEXT_DARK, fontWeight: 'bold', pb: 1 }}>
          承認の確認
        </DialogTitle>
        <DialogContent>
          {confirmTarget?.payment_preview ? (
            <Stack spacing={1}>
              <Typography variant="body2" color={TEXT_DARK} mb={0.5}>
                {confirmTarget.store_user_name}さんの勤怠を承認します。
              </Typography>
              <Box sx={{ bgcolor: '#f0f7ff', px: 2, py: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color={TEXT_GRAY} display="block" mb={0.5}>
                  支払い金額の内訳
                </Typography>
                <Stack spacing={0.3}>
                  {confirmTarget.hourly_wage != null && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={TEXT_GRAY}>時給</Typography>
                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                        ¥{confirmTarget.hourly_wage.toLocaleString()}/h
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color={TEXT_GRAY}>給与</Typography>
                    <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                      ¥{confirmTarget.payment_preview.basic_wage.toLocaleString()}
                    </Typography>
                  </Stack>
                  {confirmTarget.payment_preview.overtime_extra > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={TEXT_GRAY}>法定外残業手当 (+25%)</Typography>
                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                        +¥{confirmTarget.payment_preview.overtime_extra.toLocaleString()}
                      </Typography>
                    </Stack>
                  )}
                  {confirmTarget.payment_preview.late_night_extra > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={TEXT_GRAY}>深夜手当 (+25%)</Typography>
                      <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                        +¥{confirmTarget.payment_preview.late_night_extra.toLocaleString()}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color={TEXT_GRAY}>諸手当</Typography>
                    <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                      +¥{confirmTarget.payment_preview.allowance_amount.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color={TEXT_GRAY}>交通費</Typography>
                    <Typography variant="caption" color={TEXT_DARK} fontWeight={500}>
                      +¥{confirmTarget.payment_preview.transport_amount.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Box sx={{ borderTop: '1px solid #d9eafb', pt: 0.5, mt: 0.3 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color={TEXT_DARK} fontWeight={700}>支払い合計</Typography>
                      <Typography variant="body2" color={PRIMARY_BLUE} fontWeight={700}>
                        ¥{confirmTarget.payment_preview.total.toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color={TEXT_DARK}>
              {confirmTarget?.store_user_name}さんの勤怠記録を承認して完了しますか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmTarget(null)}
            sx={{ color: TEXT_GRAY }}
          >
            キャンセル
          </Button>
          <Button
            onClick={() => confirmTarget && executeApprove(confirmTarget)}
            variant="contained"
            sx={{
              bgcolor: PRIMARY_BLUE,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0058b7', boxShadow: 'none' },
            }}
          >
            承認して完了
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceApprovalPage;
